import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col, DatePicker as AntdDatePicker, Row } from 'antd';
import moment from 'moment';
import { CloseCircleOutlined } from '@ant-design/icons';
import Icon, { Calendar } from 'components/Atoms/Icon';
import cn from 'classnames';
import { FormattedMessage } from "react-intl";
import { getTimeFormat } from 'utilities/time';
import './styles.scss';
import { isEmpty } from 'lodash';
import { customLocale } from 'constants/custom-locale';

const { RangePicker } = AntdDatePicker;

const DatePicker = ({
  label = '',
  value,
  allowClear,
  type,
  onlyPicker = false,
  className = '',
  renderExtraFooter,
  split = '→',
  defaultText = 'All',
  listFooterDate,
  ...restProps
}) => {
  const ref = useRef();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerValue, setDatePickerValue] = useState(null);

  const toggleShowDatePicker = (visible) => () => {
    setShowDatePicker(visible);
  };

  const onChange = (dates) => {
    setShowDatePicker(false);
    setDatePickerValue(dates);
    restProps.onChange && restProps.onChange(dates);
  };

  const onClearValue = () => {
    setDatePickerValue([]);
    restProps?.onChange([]);
  };

  const displayValue = useMemo(() => {
    if (isEmpty(datePickerValue)) return defaultText;

    return Array.isArray(datePickerValue)
      ? `${getTimeFormat(
          datePickerValue[0],
          restProps.format
        )} ${split} ${getTimeFormat(datePickerValue[1], restProps.format)} `
      : getTimeFormat(datePickerValue);
  }, [datePickerValue]);

  const generateExtraFooter = useCallback(() => {
    const currentDate = moment();
    listFooterDate = listFooterDate || [
      {
        value: [currentDate, currentDate],
        name: <FormattedMessage id="date_picker.today" />,
      },
      {
        value: [currentDate.clone().subtract(1, 'months'), currentDate],
        name: <FormattedMessage id="date_picker.last_month" />,
      },
      {
        value: [currentDate.clone().subtract(2, 'months'), currentDate],
        name: <FormattedMessage id="date_picker.last_60_days" />,
      },
      {
        value: [currentDate.clone().subtract(3, 'months'), currentDate],
        name: <FormattedMessage id="date_picker.last_90_days" />,
      },
    ];
    return (
      <Row gutter={[10, 10]}>
        {listFooterDate.map((e, i) => (
          <Col key={`${e.name}-${i}`}>
            <span className="extra-item" onClick={() => onChange(e.value)}>
              {e.name}
            </span>
          </Col>
        ))}
      </Row>
    );
  });

  const suffixIconCustom = useMemo(() => {
    if (!isEmpty(datePickerValue) && allowClear) {
      return (
        <Icon
          className="icon-clear-custom"
          component={CloseCircleOutlined}
          onClick={onClearValue}
        />
      );
    }
    return <Icon component={Calendar} />;
  }, [datePickerValue, allowClear]);

  const onOpenChange = (open) => {
    setShowDatePicker(open);
  };

  useEffect(() => {
    if (!isEmpty(value)) setDatePickerValue(value);
  }, [value]);

  return (
    <div className={cn('custom-picker', className)}>
      {!showDatePicker && !onlyPicker ? (
        <div
          className="select-date-picker"
          onClick={toggleShowDatePicker(true)}
        >
          <div className="wrapper">
            {label && <span className="label">{label}:</span>}
            <span className="value">{displayValue}</span>
          </div>
          <div className="suffix-icon">{suffixIconCustom}</div>
        </div>
      ) : (
        <RangePicker
          ref={ref}
          {...restProps}
          renderExtraFooter={generateExtraFooter}
          showToday={false}
          allowClear={false}
          suffixIcon={<Icon component={Calendar} />}
          open={showDatePicker}
          onChange={onChange}
          onOpenChange={onOpenChange}
          format={restProps.format}
          value={datePickerValue}
          dropdownClassName={cn('panel-container', restProps.dropdownClassName)}
          locale={customLocale}
        />
      )}
    </div>
  );
};

export default DatePicker;
